import noop from 'lodash/noop';

import { AnchorHTMLAttributes, forwardRef } from 'react';

import { Link, NavLink } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useAppState } from '@/store/app-state';

interface NavigationIconButtonProps
  extends AnchorHTMLAttributes<HTMLAnchorElement> {
  route: {
    label: string;
    href: string;
    icon: React.ReactNode;
    strictMatch?: boolean;
    disabled?: boolean;
  };
}

const NavigationIconLink = forwardRef<
  HTMLAnchorElement,
  NavigationIconButtonProps
>(({ route, onClick = noop, ...props }, ref) => {
  const { navigationPanelOpen } = useAppState();

  const iconClasses = [
    'absolute left-0 top-0 flex rounded items-center h-10 w-10 items-center justify-center text-foreground/70', // Layout
    'transition-colors',
  ];

  const classes = [
    'relative',
    'h-10 w-10 group-data-[state=expanded]:w-full',
    'transition-all duration-200',
    'flex items-center rounded',
    'group-data-[state=collapsed]:justify-center',
    'group-data-[state=expanded]:-space-x-2',
    'hover:bg-muted',
    'group/item',
  ];

  return (
    <NavLink
      role="button"
      ref={ref}
      to={route.href!}
      {...props}
      onClick={onClick}
      end={route.strictMatch}
      aria-disabled={route.disabled}
      className={cn(route.disabled && 'pointer-events-none', props.className)}
    >
      {({ isActive }) => (
        <div
          aria-current={isActive}
          className={cn(
            classes,
            isActive && '!bg-secondary shadow-sm',
            route.disabled && 'opacity-30',
            props.className
          )}
        >
          <span
            id="icon-link"
            className={cn(...iconClasses, isActive && '!text-primary')}
            {...props}
          >
            {route.icon}
          </span>
          <span
            aria-hidden={navigationPanelOpen || undefined}
            className={cn(
              'min-w-[128px] text-sm text-foreground/90',
              'group-hover/item:text-foreground',
              'group-aria-current/item:text-foreground',
              'absolute left-7 group-data-[state=expanded]:left-12',
              'opacity-0 group-data-[state=expanded]:opacity-100',
              `${isActive && 'text-foreground hover:text-foreground'}`,
              'transition-all'
            )}
          >
            {route.label}
          </span>
        </div>
      )}
    </NavLink>
  );
});

NavigationIconLink.displayName = 'NavigationIconLink';
export default NavigationIconLink;
